import { Add, Delete } from '@mui/icons-material';
import { Theme, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import {
    Button as SMButton,
    Page,
    PageTitleWithButton,
    Pagination,
    Table
} from '../../../components';
import { deletePlanRoom, getAdminPlanRooms } from '../../../fetch';
import { IPlanRoom } from '../../../models/plan-rooms';
import { ManagePlanRoomsModal } from './ManagePlanRoomsModal';
import { MobilePlanRooms } from './MobilePlanRooms';

export const ManagePlanRooms = () => {
    const classes = useStyles();

    const [planRooms, setPlanRooms] = useState<IPlanRoom[]>([]);
    const [page, setPage] = useState<number>(0);
    const [recordCount, setRecordCount] = useState<number>(0);
    const [perPage, setRowsPerPage] = useState<number>(10);
    const [isLoadingPlanRooms, setIsLoadingPlanRooms] = useState(true);
    const isMobile = useMediaQuery('(max-width: 960px)');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleting, setDeleting] = useState<boolean>(false);
    const { enqueueSnackbar } = useSnackbar();

    const fetchPlanRooms = async () => {
        try {
            setIsLoadingPlanRooms(true);
            const res = await getAdminPlanRooms({
                page: page + 1,
                perPage,
                sortBy: 'Category'
            });
            setRecordCount(res.totalRecordCount);
            setPlanRooms(res.records);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingPlanRooms(false);
        }
    };

    useEffect(() => {
        fetchPlanRooms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage]);

    const handleAddPlanRoom = () => {
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
    };

    const handleDelete = async (id) => {
        const result = window.confirm('Are you sure you want to delete this Plan Room?');
        if (result) {
            setDeleting(true);
            try {
                await deletePlanRoom(id);
                fetchPlanRooms();
                enqueueSnackbar('Plan Room Deleted!', {
                    variant: 'success'
                });
            } catch (error) {
                let potentialErrorMessage = error?.response?.data?.Detail;
                if (potentialErrorMessage) {
                    enqueueSnackbar(potentialErrorMessage, {
                        variant: 'error'
                    });
                } else {
                    enqueueSnackbar('Unable to delete Plan Room.', {
                        variant: 'error'
                    });
                }
            } finally {
                setDeleting(false);
            }
        }
    };

    const columns = useMemo(() => {
        return [
            {
                Header: 'Plan Room Name',
                accessor: 'name',

                sort: false
            },
            {
                Header: 'Category',
                accessor: 'category',

                sort: false
            },
            {
                id: 'actions',
                accessor: '',
                Header: '',
                sort: false,

                Cell: ({
                    cell: {
                        row: { original }
                    }
                }: {
                    cell: { row: { original: IPlanRoom } };
                }) => {
                    return (
                        <div className={classes.actionButtons}>
                            <SMButton
                                id="delete"
                                className={classes.deleteButton}
                                startIcon={<Delete />}
                                onClick={() => {
                                    handleDelete(original.id);
                                }}
                            >
                                Delete
                            </SMButton>
                        </div>
                    );
                }
            }
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planRooms]);

    return (
        <>
            <Page title="Manage Plan Rooms">
                <PageTitleWithButton
                    pageTitleBackProps={{
                        showDivider: false,
                        backText: 'Back to Admin home',
                        backUrl: '/admin',
                        title: 'Manage Plan Rooms'
                    }}
                    button={
                        <SMButton
                            id="create-PlanRoom"
                            startIcon={<Add />}
                            onClick={() => handleAddPlanRoom()}
                            color="primary"
                            variant="contained"
                            className={classes.addRoleButton}
                        >
                            Add Plan Room
                        </SMButton>
                    }
                />
                <Table
                    columns={columns}
                    data={planRooms}
                    hidePagination
                    isLoading={isLoadingPlanRooms || isDeleting}
                mobileProps={{
                  handleDelete: (id: number) => handleDelete(id)
                }}
                ResponsiveComponent={isMobile ? MobilePlanRooms : undefined}
                />
                {!isLoadingPlanRooms && (
                    <Pagination
                        page={page}
                        count={recordCount}
                        rowsPerPage={perPage}
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                )}
                <ManagePlanRoomsModal
                    open={isModalOpen}
                    onClose={() => handleModalClose()}
                    fetchPlanRooms={fetchPlanRooms}
                />
            </Page>
        </>
    );
};
const useStyles = makeStyles((theme: Theme) => ({
    wrapper: {
        marginTop: '1rem'
    },
    link: {
        textDecoration: 'none'
    },
    actionButtons: {
        textAlign: 'right'
    },
    addRoleButton: {
        marginTop: theme.spacing(1),
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            marginTop: 0,
            width: 'auto'
        }
    },
    addRoleWrapper: {
        marginBottom: theme.spacing(1)
    },
    deleteButton: {
        marginLeft: theme.spacing(0.5),
        color: theme.palette.error.main
    }
}));
